import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout } from '../components/layout';
import { Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';

const SITE_URL = 'https://www.patekarecycle.com';

type TabId = 'quote' | 'audit' | 'donate';

const tabs: { id: TabId; label: string }[] = [
  { id: 'quote', label: 'Get Quote' },
  { id: 'audit', label: 'Request Assessment' },
  { id: 'donate', label: 'Donate Waste' },
];

const serviceOptions = [
  'Scrapyard & Factory Scrap Management',
  'Plastic Waste Management',
  'Expired Stock Disposal',
  'Liquidation of Stocks',
  'E-Scrap and E-waste Management',
  'Expired Cosmetics & Pharmaceuticals Waste',
  'EPR Services for Plastic and E-waste',
  'Scrap Trading',
  'Other',
];

const wasteTypeOptions = [
  'Old Garments',
  'Toys & Games',
  'E-waste / Electronics',
  'Books & Stationery',
  'Other',
];

const businessSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  company: z.string().optional(),
  service: z.string().optional(),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

const donateSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().min(6, 'Please enter a valid phone number'),
  email: z.string().email('Please enter a valid email address'),
  wasteType: z.string().min(1, 'Please select a waste type'),
  address: z.string().min(5, 'Please enter your pickup address'),
  description: z.string().optional(),
});

type BusinessFormData = z.infer<typeof businessSchema>;
type DonateFormData = z.infer<typeof donateSchema>;

function BusinessForm({ tab }: { tab: 'quote' | 'audit' }) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<BusinessFormData>({
    resolver: zodResolver(businessSchema),
  });

  const onSubmit = async (data: BusinessFormData) => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log('Form submitted:', data);
      setIsSubmitted(true);
      reset();
    } catch {
      setSubmitError('Failed to send. Please email us directly at info@patekarecycle.com');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-p-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-p-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
        <p className="text-gray-400 mb-6">We'll get back to you within 24 hours.</p>
        <button
          onClick={() => { setIsSubmitted(false); setSubmitError(null); }}
          className="px-6 py-2.5 bg-p-600 text-white font-medium rounded-lg hover:bg-p-700 transition-colors text-sm"
        >
          Send Another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h3 className="text-lg font-bold text-white mb-5">
        {tab === 'quote' ? 'Tell us about your waste' : 'Request a waste audit'}
      </h3>

      {submitError && (
        <div className="p-4 bg-red-900/30 border border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-300 text-sm">{submitError}</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <input {...register('name')} placeholder="Full Name *"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
          {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name.message}</p>}
        </div>
        <div>
          <input {...register('phone')} placeholder="Phone Number"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
        </div>
        <div className="sm:col-span-2">
          <input {...register('email')} placeholder="Email Address *"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
          {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>}
        </div>
        <div className="sm:col-span-2">
          <input {...register('company')} placeholder="Company Name"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
        </div>
        <div className="sm:col-span-2">
          <select {...register('service')}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm">
            <option value="">Select a service</option>
            {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <textarea {...register('message')} rows={4} placeholder="Describe your waste management needs... *"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent resize-none text-sm" />
          {errors.message && <p className="mt-1 text-xs text-red-400">{errors.message.message}</p>}
        </div>
      </div>

      <button type="submit" disabled={isLoading}
        className="w-full py-3.5 bg-p-600 text-white font-semibold rounded-lg hover:bg-p-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
        {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Sending...</> : <><Send className="w-4 h-4" />{tab === 'quote' ? 'Request Quote' : 'Request Assessment'}</>}
      </button>
    </form>
  );
}

function DonateForm() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<DonateFormData>({
    resolver: zodResolver(donateSchema),
  });

  const onSubmit = async (data: DonateFormData) => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log('Donate form submitted:', data);
      setIsSubmitted(true);
      reset();
    } catch {
      setSubmitError('Failed to send. Please email us directly at info@patekarecycle.com');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-p-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-p-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Pickup Scheduled!</h3>
        <p className="text-gray-400 mb-6">We'll confirm your pickup within 24 hours.</p>
        <button
          onClick={() => { setIsSubmitted(false); setSubmitError(null); }}
          className="px-6 py-2.5 bg-p-600 text-white font-medium rounded-lg hover:bg-p-700 transition-colors text-sm"
        >
          Schedule Another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h3 className="text-lg font-bold text-white mb-5">Schedule a free pickup</h3>

      {submitError && (
        <div className="p-4 bg-red-900/30 border border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-300 text-sm">{submitError}</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <input {...register('name')} placeholder="Full Name *"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
          {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name.message}</p>}
        </div>
        <div>
          <input {...register('phone')} placeholder="Phone Number *"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
          {errors.phone && <p className="mt-1 text-xs text-red-400">{errors.phone.message}</p>}
        </div>
        <div className="sm:col-span-2">
          <input {...register('email')} placeholder="Email Address *"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
          {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>}
        </div>
        <div className="sm:col-span-2">
          <select {...register('wasteType')}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm">
            <option value="">Waste Type *</option>
            {wasteTypeOptions.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          {errors.wasteType && <p className="mt-1 text-xs text-red-400">{errors.wasteType.message}</p>}
        </div>
        <div className="sm:col-span-2">
          <textarea {...register('address')} rows={2} placeholder="Pickup Address *"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent resize-none text-sm" />
          {errors.address && <p className="mt-1 text-xs text-red-400">{errors.address.message}</p>}
        </div>
        <div className="sm:col-span-2">
          <textarea {...register('description')} rows={3} placeholder="Quantity / Description (optional)"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent resize-none text-sm" />
        </div>
      </div>

      <button type="submit" disabled={isLoading}
        className="w-full py-3.5 bg-p-600 text-white font-semibold rounded-lg hover:bg-p-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
        {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Scheduling...</> : <><Send className="w-4 h-4" />Schedule Pickup</>}
      </button>
    </form>
  );
}

export function Contact() {
  const [searchParams] = useSearchParams();
  const initialTab: TabId =
    searchParams.get('donate') === 'true' ? 'donate' :
    searchParams.get('audit') === 'true' ? 'audit' : 'quote';
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);

  const tabTitle = activeTab === 'quote' ? 'Get Quote' : activeTab === 'audit' ? 'Request Assessment' : 'Donate Waste';

  return (
    <Layout>
      <Helmet>
        <title>{tabTitle} | Punarvaa by Patekar Recycle</title>
        <meta name="title" content={`${tabTitle} | Punarvaa by Patekar Recycle`} />
        <meta name="description" content="Contact Punarvaa for waste management solutions. Phone: +91 93209 37373. Email: info@patekarecycle.com. Vahuli Village, Vadpe Bhiwandi, Thane, Maharashtra." />
        <meta name="keywords" content="contact Punarvaa, waste management quote, Patekar Recycle contact, waste recovery India" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/contact`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/contact`} />
        <meta property="og:title" content={`${tabTitle} | Punarvaa`} />
        <meta property="og:description" content="Contact Punarvaa for waste management solutions. Phone: +91 93209 37373. Email: info@patekarecycle.com." />
        <meta property="og:site_name" content="Punarvaa by Patekar Recycle" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@patekarrecycle" />
        <meta name="twitter:title" content={`${tabTitle} | Punarvaa`} />
        <meta name="twitter:description" content="Contact Punarvaa for waste management solutions. Phone: +91 93209 37373. Email: info@patekarecycle.com." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Full-page dark hero */}
      <section className="min-h-screen pt-24 pb-16 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start pt-8">

            {/* Left — editorial contact info */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:pt-8"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-p-500 mb-6">
                Punarvaa · Patekar Recycle Solution Pvt Ltd
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display text-white leading-[0.95] mb-10">
                Let's build your waste recovery system.
              </h1>

              {/* Contact details as large typographic elements */}
              <div className="space-y-6">
                <div className="border-b border-gray-800 pb-6">
                  <p className="text-xs font-semibold tracking-widest uppercase text-gray-600 mb-2">Phone</p>
                  <a href="tel:+919320937373" className="text-2xl font-bold text-white hover:text-p-400 transition-colors">
                    +91 93209 37373
                  </a>
                </div>
                <div className="border-b border-gray-800 pb-6">
                  <p className="text-xs font-semibold tracking-widest uppercase text-gray-600 mb-2">Email</p>
                  <div className="space-y-1">
                    <a href="mailto:info@patekarecycle.com" className="block text-xl font-semibold text-white hover:text-p-400 transition-colors">
                      info@patekarecycle.com
                    </a>
                    <a href="mailto:connect@patekarecycle.com" className="block text-xl font-semibold text-white hover:text-p-400 transition-colors">
                      connect@patekarecycle.com
                    </a>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-widest uppercase text-gray-600 mb-2">Address</p>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Vahuli Village, Vadpe Bhiwandi,<br />
                    Thane – 421302
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right — dark form card */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl p-8"
            >
              {/* Mode tabs */}
              <div className="flex gap-1 mb-8 bg-white/10 backdrop-blur-sm rounded-lg p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-p-600 text-white shadow-lg shadow-p-600/30'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'donate' ? (
                <DonateForm />
              ) : (
                <BusinessForm tab={activeTab} />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom stats strip */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 divide-x divide-gray-200">
            {[
              { stat: '8+ Years', label: 'Industry Experience' },
              { stat: '8000 MT', label: 'Annual Capacity' },
              { stat: 'Pan-India', label: 'Operations' },
            ].map((item) => (
              <div key={item.stat} className="text-center px-6">
                <p className="text-2xl sm:text-3xl font-bold font-display text-gray-900">{item.stat}</p>
                <p className="text-sm text-gray-500 mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
